import { type ReactNode } from 'react';
import { Dropdown } from 'react-bootstrap';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import type { ClientCustomerListItemType, ClientCustomerType } from '@econtractor/types';
import SolarIcon from 'components/icons/SolarIcon';
import SvgEdit from 'components/svg/SvgEdit';
import SvgTrash from 'components/svg/SvgTrash';
import { useAuth } from 'context/AuthContext';
import type { NewObjectType } from 'modules/lead';
import {
    EMPTY_CUSTOMER,
    normalizeCustomerPayload,
    parseObjectCustomer,
    type NewSaleType,
} from 'modules/objects';
import { canViewSales, hasUserAccess } from 'utils/helpers/access/hasUserAccess';

export interface ClientCustomerActionClient {
    ID?: number;
    clientName?: string;
    clientPhone?: string;
    clientEmail?: string;
}

interface ClientCustomerActionProps<T extends ClientCustomerType> {
    row: T;
    onEdit: (row: T) => void;
    onDelete: (row: T) => void;
    onCreateSale?: (sale: NewSaleType) => void;
    onCreateObject?: (object: NewObjectType) => void;
    client?: ClientCustomerActionClient;
}

const getListClient = (row: ClientCustomerType): ClientCustomerActionClient | null => {
    if (!('clientName' in row) && !('clientPhone' in row) && !('clientId' in row)) return null;
    const listRow = row as ClientCustomerListItemType;
    return {
        ID: listRow.clientId,
        clientName: listRow.clientName,
        clientPhone: listRow.clientPhone,
    };
};

const ClientCustomerAction = <T extends ClientCustomerType>({
    row,
    onEdit,
    onDelete,
    onCreateSale,
    onCreateObject,
    client,
}: ClientCustomerActionProps<T>) => {
    const { t } = useTranslation(['Clients', 'Objects', 'Leads', 'common']);
    const { auth } = useAuth();
    const canCreateSale = canViewSales(auth?.user);
    const canCreateObject = hasUserAccess(auth?.user, 'objectCreate');
    const snapshot = getListClient(row) || client;
    const clientId = Number(row.clientId || snapshot?.ID || client?.ID || 0);
    const clientName = snapshot?.clientName || client?.clientName || '';
    const clientPhone = snapshot?.clientPhone || client?.clientPhone || '';
    const clientEmail = snapshot?.clientEmail || client?.clientEmail || '';
    const showCreateSale = Boolean(canCreateSale && onCreateSale);
    const showCreateObject = Boolean(canCreateObject && onCreateObject);

    const buildCreatePayload = (): NewSaleType => ({
        clientId,
        clientName,
        clientPhone,
        clientEmail,
        objectName: clientName || row.fullName || '',
        objectCustomer: normalizeCustomerPayload({
            ...EMPTY_CUSTOMER,
            ...parseObjectCustomer(row.customer),
            fullName: row.fullName || row.customer?.fullName || '',
            edrpouOrIpn: row.edrpouOrIpn || row.customer?.edrpouOrIpn || '',
            vatEnabled: Boolean(row.vatEnabled ?? row.customer?.vatEnabled),
        }),
    });

    const renderMenuItem = (
        icon: ReactNode,
        title: string,
        subtitle: string,
        onSelect: () => void,
        options?: { danger?: boolean },
    ) => (
        <Dropdown.Item
            as="button"
            className={options?.danger ? 'text-danger' : undefined}
            onClick={(e) => {
                onSelect();
                e.currentTarget.blur();
            }}
        >
            <div className="d-flex align-items-start w-100 text-start">
                <span
                    className={`d-inline-flex align-items-center justify-content-center flex-shrink-0 me-2 item-icon${
                        options?.danger ? ' text-danger' : ''
                    }`}
                >
                    {icon}
                </span>
                <div className="flex-grow-1 min-w-0">
                    <div className={`fw-medium ${options?.danger ? 'text-danger' : ''}`}>{title}</div>
                    <small className="text-muted warehouse-team-menu-hint">{subtitle}</small>
                </div>
            </div>
        </Dropdown.Item>
    );

    const menu = (
        <Dropdown.Menu
            className="p-2 shadow-sm warehouse-team-menu"
            popperConfig={{ strategy: 'fixed' }}
            renderOnMount
        >
            {renderMenuItem(
                <SvgEdit />,
                t('clientPopup.edit'),
                t('subjects.editHint'),
                () => onEdit(row),
            )}

            {showCreateSale || showCreateObject ? <Dropdown.Divider className="my-2" /> : null}

            {showCreateObject
                ? renderMenuItem(
                      <SolarIcon name="brightness-high" />,
                      t('Objects:toolAddObject'),
                      t('Leads:toolCreateObjectHint'),
                      () => onCreateObject?.(buildCreatePayload()),
                  )
                : null}

            {showCreateSale
                ? renderMenuItem(
                      <SolarIcon name="wallet2" />,
                      t('Objects:toolAddSale'),
                      t('Leads:toolCreateSaleHint'),
                      () => onCreateSale?.(buildCreatePayload()),
                  )
                : null}

            {showCreateSale
                ? renderMenuItem(
                      <SolarIcon name="briefcase" />,
                      t('Objects:toolAddServiceSale'),
                      t('Leads:toolCreateServiceSaleHint'),
                      () => onCreateSale?.({ ...buildCreatePayload(), onlyService: true }),
                  )
                : null}

            <Dropdown.Divider className="my-2" />

            {renderMenuItem(<SvgTrash />, t('common:buttons.delete'), t('subjects.deleteHint'), () => onDelete(row), {
                danger: true,
            })}
        </Dropdown.Menu>
    );

    return (
        <Dropdown align="end">
            <Dropdown.Toggle
                className="p-1 py-0"
                size="sm"
                variant="light-secondary"
                id={`dropdown-client-customer-${row.ID}`}
            >
                <SolarIcon name="three-dots" />
            </Dropdown.Toggle>
            {typeof document !== 'undefined' ? createPortal(menu, document.body) : menu}
        </Dropdown>
    );
};

export default ClientCustomerAction;
